import { GridRowGroups } from "../grid/GroupHeadings";
import { Fragment } from "react";
import { VisualGroupHeading } from "../VisualGroupHeading";
import { groupHeading } from "../visualGroups";
import { formatCopy } from "../../theme/copy";
import { Box } from "@mui/material";
import { portraitButtonOption, ratingSliderStyle } from "./styles";
import { DiscreteScaleSlider } from "../../components/ui";
import ActionButton from "../../components/ui/ActionButton";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import {
  compactGridCardStyle,
  compactGridListStyle,
  optionalGridFlushCellStyle,
  optionalGridLineStyle,
  optionalGridScaleInsetStyle,
  wideGridInstrumentStyle,
  wideGridTableStyle,
} from "../grid/gridInstrumentStyle";
import type {
  GridThisOrThatRatingDefinition,
  GridThisOrThatRatingDraft,
  GridThisOrThatRatingEvent,
} from "./core";

type ViewProps = QuestionViewProps<
  GridThisOrThatRatingDefinition,
  GridThisOrThatRatingDraft,
  GridThisOrThatRatingEvent
>;

const GridThisOrThatRatingView = (props: ViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const { definition, draft } = props;
  const compact = props.display.arrangement === "compact";
  const hasRowGroups = Boolean(definition.rowGroups?.length);
  const showGridLines = (theme.show_grid_lines || hasRowGroups) && !compact;
  const buttons = props.display.view === "buttons";
  const response = (
    row: GridThisOrThatRatingDefinition["rows"][number],
    rowIndex: number,
  ) => {
    const left = row[0].label;
    const right = row[row.length - 1].label;
    const selected = draft.selectedByRow[rowIndex];
    const rowName = formatCopy(theme.copy.labels.rowRange, {
      row: rowIndex + 1,
      left,
      right,
    });
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: compact ? "column" : "row",
          alignItems: compact ? undefined : "center",
          gap: `${theme.tokens.spacing["2"]}px`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {buttons ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
              }}
            >
              {row.map((point, pointIndex) => (
                <ActionButton
                  key={point.value}
                  ariaLabel={`${rowName}: ${point.value}`}
                  ariaPressed={selected === point.value}
                  disabled={!props.controls.canSubmit}
                  onClick={() =>
                    props.dispatch({
                      type: "select",
                      rowIndex,
                      value: point.value,
                    })
                  }
                  testId={`LeftRight-${rowIndex + 1}-${pointIndex + 1}`}
                  style={[
                    portraitButtonOption(
                      theme,
                      selected === point.value,
                      pointIndex === row.length - 1,
                    ),
                    showGridLines
                      ? {
                          border: 0,
                          borderLeft:
                            pointIndex === 0
                              ? 0
                              : `1px solid ${theme.tokens.color.action.primary}`,
                        }
                      : {},
                    {
                      minWidth: 0,
                      width: "100%",
                      minHeight: `${theme.tokens.spacing["6"] * 2}px`,
                      margin: 0,
                    },
                  ]}
                >
                  {point.value}
                </ActionButton>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                paddingTop: 1,
                ...(showGridLines
                  ? optionalGridScaleInsetStyle(theme, false, showGridLines)
                  : { paddingX: 1 }),
              }}
            >
              <DiscreteScaleSlider
                testId={`slider-${rowIndex}`}
                ariaLabel={formatCopy(theme.copy.labels.rowRatingScale, {
                  row: rowName,
                })}
                points={row}
                selectedValue={selected}
                disabled={!props.controls.canSubmit}
                onSelect={(value) =>
                  props.dispatch({ type: "select", rowIndex, value })
                }
                sx={ratingSliderStyle(theme)}
                centerWhenUnanswered
              />
            </Box>
          )}
        </Box>
        {compact && definition.dontKnow && (
          <ActionButton
            currentVal={{ text: definition.dontKnow.label, display_text: true }}
            isSelected={selected === definition.dontKnow.value}
            ariaPressed={selected === definition.dontKnow.value}
            ariaDisabled={!props.controls.canSubmit}
            onClick={() => props.dispatch({ type: "dont-know", rowIndex })}
            testId={`grid-this-or-that-rating-dont-know-${rowIndex}`}
          />
        )}
      </Box>
    );
  };

  return (
    <Box
      data-testid="grid-this-or-that-rating-view"
      data-grid-ready="true"
      sx={{
        width: "100%",
        ...(compact
          ? {}
          : wideGridInstrumentStyle(
              theme,
              theme.show_grid_lines,
              hasRowGroups,
            )),
      }}
    >
      {compact ? (
        <Box sx={compactGridListStyle(theme)}>
          {definition.rows.map((row, rowIndex) => (
            <Fragment key={rowIndex}>
              <VisualGroupHeading
                text={groupHeading(
                  definition.rowGroups,
                  definition.rowIds?.[rowIndex],
                )}
              />
              <Box sx={compactGridCardStyle(theme)}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    marginBottom: 1,
                  }}
                >
                  <Box>{row[0].label}</Box>
                  <Box>{row[row.length - 1].label}</Box>
                </Box>
                {response(row, rowIndex)}
              </Box>
            </Fragment>
          ))}
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={wideGridTableStyle}>
            <colgroup>
              <col
                style={{
                  width: `${definition.dontKnow ? 18 : 22}%`,
                }}
              />
              <col
                style={{
                  width: `${definition.dontKnow ? 42 : 56}%`,
                }}
              />
              <col
                style={{
                  width: `${definition.dontKnow ? 18 : 22}%`,
                }}
              />
              {definition.dontKnow && <col style={{ width: "22%" }} />}
            </colgroup>
            <GridRowGroups
              rows={definition.rows}
              groups={definition.rowGroups}
              rowId={(_, index) => definition.rowIds?.[index]}
              columns={3 + Number(Boolean(definition.dontKnow))}
            >
              {(row, rowIndex) => (
                <Box component="tr">
                  <Box
                    component="th"
                    scope="row"
                    sx={{
                      textAlign: "right",
                      fontWeight: theme.tokens.typography.weight.regular,
                      paddingRight: `${theme.tokens.spacing["2"]}px`,
                      ...optionalGridLineStyle(theme),
                    }}
                  >
                    {row[0].label}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      paddingBlock: `${theme.tokens.spacing["2"]}px`,
                      ...optionalGridLineStyle(theme),
                      ...optionalGridFlushCellStyle(theme, showGridLines),
                    }}
                  >
                    {response(row, rowIndex)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      paddingLeft: `${theme.tokens.spacing["2"]}px`,
                      ...optionalGridLineStyle(theme),
                    }}
                  >
                    {row[row.length - 1].label}
                  </Box>
                  {definition.dontKnow && (
                    <Box
                      component="td"
                      sx={{
                        textAlign: "right",
                        ...optionalGridLineStyle(theme),
                      }}
                    >
                      <ActionButton
                        currentVal={{
                          text: definition.dontKnow.label,
                          display_text: true,
                        }}
                        isSelected={
                          draft.selectedByRow[rowIndex] ===
                          definition.dontKnow.value
                        }
                        ariaPressed={
                          draft.selectedByRow[rowIndex] ===
                          definition.dontKnow.value
                        }
                        ariaDisabled={!props.controls.canSubmit}
                        onClick={() =>
                          props.dispatch({ type: "dont-know", rowIndex })
                        }
                        testId={`grid-this-or-that-rating-dont-know-${rowIndex}`}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </GridRowGroups>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const GridThisOrThatRatingButtonsView = GridThisOrThatRatingView;
export const GridThisOrThatRatingSliderView = GridThisOrThatRatingView;
